import React, { useState, useEffect } from 'react';
import api from "../../services/api";
import { LuActivity, LuFileText, LuRefreshCw, LuChevronLeft, LuChevronRight, LuEye, LuTrash2, LuDownload } from "react-icons/lu";
import { toast } from "react-toastify";



const ConfirmationToast = ({ closeToast, onConfirm, message }) => (
    <div class="toast-body">
        {message}
        <div class="mt-2 pt-2 border-top">
            <button type="button" class="btn btn-primary btn-sm" onClick={() => { onConfirm(); closeToast(); }}>Delete</button>
            <button type="button" class="btn btn-secondary btn-sm ms-2" onClick={closeToast}>Close</button>
        </div>
    </div>
);

const LogViewer = ({ isSidebarOpen = true }) => {
    const [parsedLogs, setParsedLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedLog, setSelectedLog] = useState(null);

    // Filters & Pagination
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [actionFilter, setActionFilter] = useState('All');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const [limit, setLimit] = useState(10);

    const fetchLogs = async () => {
        try {
            setLoading(true);
            const params = { page, limit };
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;
            if (actionFilter && actionFilter !== 'All') params.action = actionFilter;

            const response = await api.get('/logs/llm', { params });

            if (response.data.success) {
                setParsedLogs(response.data.data);
                if (response.data.pagination) {
                    setTotalPages(response.data.pagination.pages);
                    setTotalCount(response.data.pagination.total || 0);
                }
            } else {
                toast.error(response.data.message);
                setParsedLogs([]);
            }
        } catch (error) {
            console.error("Error fetching logs:", error);
            setParsedLogs([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setPage(1); // Reset to page 1 on filter/limit change
    }, [startDate, endDate, actionFilter, limit]);

    useEffect(() => {
        fetchLogs();
    }, [startDate, endDate, actionFilter, page, limit]);

    const getBadgeClass = (decision) => {
        if (!decision) return "bg-secondary";
        const d = decision.toLowerCase();
        if (d.includes("buy")) return "bg-success";
        if (d.includes("sell")) return "bg-danger";
        return "bg-secondary";
    };

    const openLogModal = (log) => {
        setSelectedLog(log);
    };

    const resetFilters = () => {
        setStartDate('');
        setEndDate('');
        setActionFilter('All');
    };

    const handleDeleteLogs = () => {
        const confirmDelete = async () => {
            try {
                setLoading(true);
                const response = await api.post('/logs/delete', { logType: 'llm' });
                if (response.data.success) {
                    toast.success(response.data.message);
                    fetchLogs();
                } else {
                    toast.error(response.data.message);
                }
            } catch (error) {
                console.error("Error deleting logs:", error);
                toast.error("Failed to delete logs");
            } finally {
                setLoading(false);
            }
        };

        toast.error(
            <ConfirmationToast
                onConfirm={confirmDelete}
                message="Are you sure you want to delete ?"
            />,
            {
                position: "top-center",
                autoClose: false,
                closeOnClick: false,
                draggable: false,
                closeButton: false
            }
        );
    };

    const handleDownloadLogs = async () => {
        try {
            const response = await api.get('/logs/download', {
                params: { logType: 'llm' },
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'llm_logs.log');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            console.error("Error downloading logs:", error);
            toast.error("Failed to download logs");
        }
    };

    // Helper to generate page numbers
    const paginationRange = () => {
        const totalPageCount = totalPages;
        const siblingCount = 1;
        const boundaryCount = 3;

        if (totalPageCount <= 10) {
            const range = [];
            for (let i = 1; i <= totalPageCount; i++) range.push(i);
            return range;
        }

        const leftSiblingIndex = Math.max(page - siblingCount, 1);
        const rightSiblingIndex = Math.min(page + siblingCount, totalPageCount);

        const shouldShowLeftDots = leftSiblingIndex > boundaryCount + 1;
        const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

        if (!shouldShowLeftDots && shouldShowRightDots) {
            const leftItemCount = 3 + 2 * siblingCount;
            let leftRange = [];
            for (let i = 1; i <= Math.max(leftItemCount, rightSiblingIndex); i++) leftRange.push(i);
            return [...leftRange, "...", totalPageCount];
        }

        if (shouldShowLeftDots && !shouldShowRightDots) {
            const rightItemCount = 3 + 2 * siblingCount;
            let rightRange = [];
            for (let i = totalPageCount - rightItemCount + 1; i <= totalPageCount; i++) rightRange.push(i);
            return [1, 2, 3, "...", ...rightRange];
        }

        if (shouldShowLeftDots && shouldShowRightDots) {
            let middleRange = [];
            for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) middleRange.push(i);
            return [1, 2, 3, "...", ...middleRange, "...", totalPageCount];
        }
    };

    return (
        <div className={`${isSidebarOpen ? "col-lg-9" : "col-lg-12"} pt-4 pb-2 pb-sm-4`}>
            <div className="container">
                <div className="row">
                    <div className="col-12 mb-4">
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                            <h1 className="h3 mb-0 text-gray-800">Logs</h1>
                        </div>
                    </div>

                    <div className="col-12">
                        <div className="card border-0 py-1 p-md-2 p-xl-3 p-xxl-4 mb-4">
                            <div className="card-body">
                                <h6 className="fw-bold mb-3">Filters</h6>
                                <div className="row g-3">
                                    <div className="col-12 col-sm-6 col-lg-3">
                                        <label className="form-label small text-muted mb-1">Start Date</label>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-12 col-sm-6 col-lg-3">
                                        <label className="form-label small text-muted mb-1">End Date</label>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-12 col-sm-6 col-lg-3">
                                        <label className="form-label small text-muted mb-1">Action</label>
                                        <select
                                            className="form-select form-select-sm"
                                            value={actionFilter}
                                            onChange={(e) => setActionFilter(e.target.value)}
                                        >
                                            <option value="All">All Actions</option>
                                            <option value="Buy">Buy</option>
                                            <option value="Sell">Sell</option>
                                            <option value="Hold">Hold</option>
                                        </select>
                                    </div>
                                    <div className="col-12 col-sm-6 col-lg-3 d-flex align-items-end">
                                        <div className="d-flex gap-2 w-100">
                                            <button className="btn btn-sm btn-primary w-50 d-flex align-items-center justify-content-center gap-2" onClick={fetchLogs} disabled={loading}>
                                                <LuRefreshCw className={loading ? "spin-icon" : ""} size={14} />Refresh
                                            </button>
                                            <button className="btn btn-sm btn-outline-primary w-50" onClick={resetFilters} disabled={loading}>
                                                Reset
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div className="text-muted small">
                                <strong>Total Logs: {totalCount}</strong>
                                <span className="mx-2">|</span>
                                Showing {(page - 1) * limit + 1} - {Math.min(page * limit, totalCount)}
                            </div>
                            <div className="text-muted small">
                                Page {page} of {totalPages}
                            </div>
                        </div>
                    </div>

                    <div className="col-12">
                        <section className="card border-0 py-1 p-md-2 p-xl-3 p-xxl-4 mb-4">
                            <div className="card-body">
                                <div className="d-flex align-items-center justify-content-between pb-3">
                                    <div className="d-flex align-items-center">
                                        <LuActivity className="text-primary lead pe-1 me-2" size={24} />
                                        <h2 className="h5 mb-0">Trade Decisions</h2>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <button className="btn btn-sm btn-outline-info d-flex align-items-center gap-2" onClick={handleDownloadLogs} disabled={loading}>
                                            <LuDownload size={14} /> Download
                                        </button>
                                        <button className="btn btn-sm btn-danger d-flex align-items-center gap-2" onClick={handleDeleteLogs} disabled={loading}>
                                            <LuTrash2 size={14} /> Clear Logs
                                        </button>
                                    </div>
                                </div>

                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="text-muted mt-2">Loading logs...</p>
                                    </div>
                                ) : parsedLogs.length === 0 ? (
                                    <div className="text-center py-5">
                                        <h5 className="text-muted">No logs found. Try adjusting your filters.</h5>
                                    </div>
                                ) : (
                                    <>
                                        <div className="table-responsive">
                                            <table className="table table-hover mb-0 align-middle">
                                                <thead>
                                                    <tr>
                                                        <th className="ps-4 rounded-start">Time</th>
                                                        <th>Decision</th>
                                                        <th>Strike</th>
                                                        <th>PCR</th>
                                                        <th className="rounded-end text-end pe-4">Details</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {parsedLogs.map((log) => (
                                                        <tr key={log.id}>
                                                            <td className="ps-4 text-body-secondary small">{new Date(log.timestamp).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</td>
                                                            <td>
                                                                <span className={`badge ${getBadgeClass(log.decision)}`}>
                                                                    {log.decision}
                                                                </span>
                                                            </td>
                                                            <td className="fw-medium">{log.strike}</td>
                                                            <td>{log.pcr}</td>
                                                            <td className="text-end pe-4">
                                                                <button
                                                                    className="btn btn-sm btn-outline-info"
                                                                    onClick={() => openLogModal(log)}
                                                                    title="View Details"
                                                                >
                                                                    <LuEye size={14} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Pagination */}
                                        <div className="d-flex align-items-center justify-content-between pt-3 pb-2 px-3 border-top">
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="text-body-secondary fs-sm">Show</span>
                                                <select
                                                    className="form-select form-select-sm form-select-flush"
                                                    style={{ width: 'auto' }}
                                                    value={limit}
                                                    onChange={(e) => setLimit(parseInt(e.target.value))}
                                                >
                                                    <option value="10">10</option>
                                                    <option value="25">25</option>
                                                    <option value="50">50</option>
                                                    <option value="100">100</option>
                                                </select>
                                            </div>

                                            <nav aria-label="Page navigation">
                                                <ul className="pagination pagination-sm mb-0">
                                                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => setPage(page - 1)}
                                                            aria-label="Prev"
                                                        >
                                                            <LuChevronLeft className="fs-xl me-1" /> Prev
                                                        </button>
                                                    </li>

                                                    {paginationRange().map((p, idx) => (
                                                        <li key={idx} className={`page-item ${p === page ? 'active' : ''} ${p === '...' ? 'disabled' : ''}`}>
                                                            <button
                                                                className="page-link"
                                                                onClick={() => p !== '...' && setPage(p)}
                                                                style={{ cursor: p === '...' ? 'default' : 'pointer' }}
                                                            >
                                                                {p}
                                                            </button>
                                                        </li>
                                                    ))}

                                                    <li className={`page-item ${page === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => setPage(page + 1)}
                                                            aria-label="Next"
                                                        >
                                                            Next <LuChevronRight className="fs-xl ms-1" />
                                                        </button>
                                                    </li>
                                                </ul>
                                            </nav>
                                        </div>
                                    </>
                                )}
                            </div>
                        </section>
                    </div>
                </div>
            </div>

            {/* React State Modal */}
            {selectedLog && (
                <div className={`modal fade ${selectedLog ? 'show d-block' : ''}`} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                    <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Log Details</h5>
                                <button type="button" className="btn-close" onClick={() => setSelectedLog(null)} aria-label="Close"></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <span className={`badge ${getBadgeClass(selectedLog.decision)} me-2`}>{selectedLog.decision}</span>
                                    <span className="text-muted small">{new Date(selectedLog.timestamp).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</span>
                                </div>
                                <div className="bg-light p-3 rounded font-monospace" style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
                                    {selectedLog.fullLog}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setSelectedLog(null)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .spin-icon { animation: spin 1s linear infinite; }
                @keyframes spin { 100% { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
};

export default LogViewer;
